/*
 * Extracted from WalletPage, which had grown to 920 lines around four
 * module-level components. They were files in everything but name: each has
 * its own state, its own Stripe calls and its own error surface, and none of
 * them referenced the others' internals.
 */
import { useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { useNotifications } from '@/app/providers/notifications';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { Button, Card, CardHeader } from '@/shared/ui';
import { BillingAddressForm } from '@/features/wallet/components/BillingAddressForm';
import { newIdempotencyKey, setBillingAddress, topUp } from '@/features/wallet/services/walletService';
import { ApiError } from '@/shared/api/apiClient';
import { safeErrorMessage, stripeErrorMessage, UserFacingError } from '@/shared/utils/errors';
import { MAX_TOPUP_USD, parseTopUpAmount } from '@/features/wallet/utils/topupAmount';
import { formatCredits, formatUsd } from '@/shared/utils/format';
import type { BillingAddress, PaymentMethodSummary, WalletBalance } from '@/shared/types';
import { poll } from '@/features/wallet/utils/poll';

const PRESET_AMOUNTS = [10, 25, 50, 100];

// A caught top-up ApiError can be the 402 "needs 3DS" variant, which carries the
// client_secret to complete client-side. Returns that secret, or null otherwise.
function authRequiredSecret(err: unknown): string | null {
  if (err instanceof ApiError && err.status === 402 && err.detail && typeof err.detail === 'object') {
    const detail = err.detail as { error?: string; client_secret?: string | null };
    if (detail.error === 'payment_authentication_required') return detail.client_secret ?? null;
  }
  return null;
}

// Both address 409s route to the same place — the form — but with different copy:
// "add your address" is confusing when one is already on file and merely
// unplaceable. Returns the code, or null if this is some other failure.
function addressErrorCode(err: unknown): 'billing_address_required' | 'billing_address_invalid' | null {
  if (err instanceof ApiError && err.status === 409 && err.detail && typeof err.detail === 'object') {
    const code = (err.detail as { error?: string }).error;
    if (code === 'billing_address_required' || code === 'billing_address_invalid') return code;
  }
  return null;
}

export function WalletAddFundsSection({
  balance,
  paymentMethod,
  refreshBalance,
}: {
  balance: WalletBalance | null;
  paymentMethod: PaymentMethodSummary | null;
  refreshBalance: () => Promise<WalletBalance | null>;
}) {
  const stripe = useStripe();
  const { pushNotification } = useNotifications();
  const [selectedAmount, setSelectedAmount] = useState<number | 'custom'>(PRESET_AMOUNTS[0]);
  const [customAmount, setCustomAmount] = useState('');
  const [topUpError, setTopUpError] = useState<string | null>(null);
  const [isToppingUp, setIsToppingUp] = useState(false);
  // Set when a top-up is refused for its address. Holds the amount and the
  // ORIGINAL idempotency key so the post-address retry is the same attempt
  // rather than a second one.
  const [addressPrompt, setAddressPrompt] = useState<{
    amount: number;
    idempotencyKey: string;
    reason: 'billing_address_required' | 'billing_address_invalid';
  } | null>(null);
  const [addressSaveError, setAddressSaveError] = useState<string | null>(null);

  const resetAmount = () => {
    setSelectedAmount(PRESET_AMOUNTS[0]);
    setCustomAmount('');
  };

  // Honest post-charge notification: only claim the credits landed if the credited
  // balance actually moved within the poll window; otherwise say it's still
  // processing so we never show a confirmation the balance doesn't back up.
  // $1 = 1 credit, so the dollars charged ARE the credits added.
  const notifyCredited = (credited: WalletBalance | null | undefined, amount: number, before: number) => {
    const landed = (credited?.balanceCredits ?? 0) > before;
    pushNotification({
      message: landed
        ? `Added ${formatCredits(amount)} credits to your wallet`
        : 'Top-up received — your credits will update shortly.',
    });
  };

  /* `before` is null when the pre-charge balance was never known (the wallet's
     initial load failed and the retry at click time failed too). With no
     baseline, "did the credits land" has no answer — `?? 0` used to stand in,
     and any non-zero balance then passed the test on the first read, firing the
     confident "Added N credits" over a balance that had not moved. No baseline
     means no poll: refresh once so the page catches up, and take the honest
     wording. */
  const settleAndNotify = async (amount: number, before: number | null) => {
    if (before === null) {
      void refreshBalance().catch(() => null);
      pushNotification({ message: 'Top-up received — your credits will update shortly.' });
    } else {
      const credited = await poll(refreshBalance, (b) => (b?.balanceCredits ?? 0) > before);
      notifyCredited(credited, amount, before);
    }
    resetAmount();
  };

  // Runs 3DS client-side, waits for the credited balance, then notifies. Throws on
  // a Stripe failure (declined, cancelled auth, ...) so the caller surfaces it.
  const completeThreeDS = async (clientSecret: string, amount: number, before: number | null) => {
    if (!stripe) throw new UserFacingError('The payment form is still loading. Please try again in a moment.');
    const { error } = await stripe.confirmCardPayment(clientSecret);
    // Card/validation messages are user-ready, anything else gets catalog copy;
    // UserFacingError lets the result survive safeErrorMessage at the catch sites.
    if (error) {
      throw new UserFacingError(
        stripeErrorMessage(error, 'Card authentication failed. Please try again.'),
      );
    }
    await settleAndNotify(amount, before);
  };

  // One attempt at a specific idempotency key, plus every outcome it can have.
  // Extracted so the post-address retry is literally the same code path as the
  // first try — the retry is a resumption of one attempt, not a new one.
  const runTopUp = async (amount: number, idempotencyKey: string, before: number | null) => {
    const result = await topUp(amount, idempotencyKey);
    if (result.status === 'captured') {
      await settleAndNotify(amount, before);
    } else if (result.status === 'requires_action') {
      if (!result.clientSecret) {
        // 200 requires_action but no secret to complete with — can't proceed.
        setTopUpError("We couldn't start card authentication for this payment. Please try again.");
      } else {
        await completeThreeDS(result.clientSecret, amount, before);
      }
    } else if (result.status === 'pending') {
      pushNotification({
        message: `Top-up of ${formatUsd(amount)} is processing — your credits will update shortly.`,
      });
      // Background catch-up only; with no baseline there is nothing to poll toward.
      void (before === null
        ? refreshBalance().catch(() => null)
        : poll(refreshBalance, (b) => (b?.balanceCredits ?? 0) > before));
      resetAmount();
    } else {
      // canceled | failed
      setTopUpError("That top-up didn't go through. Please try again or use a different card.");
    }
  };

  // Shared failure handling for both the first attempt and the post-address retry.
  // Every branch is terminal — it sets a banner, opens the address form, or runs
  // 3DS — so there is nothing for a caller to decide afterwards and both of them
  // simply await it. (It used to be documented as returning a boolean saying
  // whether it had opened the address form. It never did.)
  const handleTopUpFailure = async (
    err: unknown,
    amount: number,
    idempotencyKey: string,
    before: number | null,
  ): Promise<void> => {
    const addressCode = addressErrorCode(err);
    if (addressCode) {
      // Not an error banner: the card is fine and there is a five-field fix, so
      // open the form and hold on to the amount + key for the retry.
      setAddressPrompt({ amount, idempotencyKey, reason: addressCode });
      return;
    }
    const secret = authRequiredSecret(err);
    if (secret) {
      try {
        await completeThreeDS(secret, amount, before);
      } catch (threeDsErr) {
        setTopUpError(
          safeErrorMessage(threeDsErr, 'Card authentication failed. Please try again.'),
        );
      }
      return;
    }
    setTopUpError(safeErrorMessage(err, 'Top-up failed. Please try again.'));
  };

  const handleTopUp = async () => {
    setTopUpError(null);
    if (!paymentMethod) {
      setTopUpError('Add a payment method before adding funds.');
      return;
    }
    // One validator for presets and custom input alike — rejects empty, junk,
    // negative, sub-$0.50, absurdly large, and normalizes sub-cent to whole cents.
    const parsed = parseTopUpAmount(selectedAmount === 'custom' ? customAmount : selectedAmount);
    if (!parsed.ok) {
      setTopUpError(parsed.message);
      return;
    }
    const amount = parsed.amountUsd;
    const idempotencyKey = newIdempotencyKey();

    setIsToppingUp(true);
    // The baseline the post-charge poll compares against. If the wallet's
    // initial load failed, try once more now — the charge is about to move this
    // number, so a real reading is worth one request. Still unknown → null, and
    // settleAndNotify takes the honest wording instead of a false pass.
    const before =
      balance?.balanceCredits ??
      (await refreshBalance().catch(() => null))?.balanceCredits ??
      null;
    try {
      await runTopUp(amount, idempotencyKey, before);
    } catch (err) {
      await handleTopUpFailure(err, amount, idempotencyKey, before);
    } finally {
      setIsToppingUp(false);
    }
  };

  // Save the address, then resume the SAME top-up attempt.
  const handleAddressSubmit = async (address: BillingAddress) => {
    const prompt = addressPrompt;
    if (!prompt) return;
    setAddressSaveError(null);
    // Same null-means-unknown baseline as handleTopUp; no retry here because the
    // address round-trip below refreshes nothing balance-related anyway.
    const before = balance?.balanceCredits ?? null;
    setIsToppingUp(true);
    try {
      await setBillingAddress(address);
    } catch (err) {
      // Keep the form open and say why — closing it would lose what they typed
      // and leave the top-up silently abandoned.
      setAddressSaveError(
        safeErrorMessage(err, "We couldn't save that address. Please try again."),
      );
      setIsToppingUp(false);
      return;
    }
    setAddressPrompt(null);
    try {
      await runTopUp(prompt.amount, prompt.idempotencyKey, before);
    } catch (err) {
      await handleTopUpFailure(err, prompt.amount, prompt.idempotencyKey, before);
    } finally {
      setIsToppingUp(false);
    }
  };

  return (
    <Card padding="lg">
      <CardHeader title="Add funds" headingLevel={2} />

      {addressPrompt && (
        <BillingAddressForm
          reason={addressPrompt.reason}
          amountUsd={addressPrompt.amount}
          busy={isToppingUp}
          error={addressSaveError}
          onSubmit={handleAddressSubmit}
          onCancel={() => {
            setAddressPrompt(null);
            setAddressSaveError(null);
          }}
        />
      )}

      <div className="wallet-amount-grid" role="group" aria-label="Top-up amount">
        {PRESET_AMOUNTS.map((amount) => (
          <button
            key={amount}
            type="button"
            className={`wallet-amount-option${selectedAmount === amount ? ' wallet-amount-option--active' : ''}`}
            aria-pressed={selectedAmount === amount}
            onClick={() => setSelectedAmount(amount)}
          >
            {formatUsd(amount)}
          </button>
        ))}
        <button
          type="button"
          className={`wallet-amount-option${selectedAmount === 'custom' ? ' wallet-amount-option--active' : ''}`}
          aria-pressed={selectedAmount === 'custom'}
          onClick={() => setSelectedAmount('custom')}
        >
          Custom
        </button>
      </div>

      {selectedAmount === 'custom' && (
        <div className="wallet-custom-amount">
          <span className="wallet-custom-amount__prefix">$</span>
          <input
            type="number"
            inputMode="decimal"
            min="0.5"
            max={String(MAX_TOPUP_USD)}
            step="0.01"
            placeholder="0.00"
            value={customAmount}
            onChange={(event) => setCustomAmount(event.target.value)}
          />
        </div>
      )}

      {/* The flat-price promise, stated where the money changes hands: what you
          pay is exactly what lands in the wallet — tax is included, never added. */}
      <p className="wallet-note">
        $1 = 1 credit — you receive exactly the credits you pay for. Taxes included
        where applicable.
      </p>

      {!paymentMethod && (
        <p className="wallet-note">Add a payment method to enable top-ups.</p>
      )}

      {topUpError && <ErrorBanner message={topUpError} onDismiss={() => setTopUpError(null)} />}

      <Button
        variant="primary"
        className="wallet-card-cta"
        onClick={handleTopUp}
        loading={isToppingUp}
        disabled={!paymentMethod}
      >
        {isToppingUp ? 'Processing…' : 'Add funds'}
      </Button>
    </Card>
  );
}
